import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, RefreshCw, Search, MessageCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { waLink, ADMIN_PIN } from "../lib/constants";
import { usePageTitle } from "../lib/seo";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "confirmed", label: "Confirmed" },
];

const STATUS_STYLE = {
  new: "border-amber-500/50 text-amber-300",
  contacted: "border-sky-500/40 text-sky-300",
  confirmed: "border-emerald-500/40 text-emerald-300",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function Admin() {
  usePageTitle("Owner's Inbox");
  const [unlocked, setUnlocked] = useState(sessionStorage.getItem("jth_admin") === "1");
  const [pin, setPin] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked]);

  const unlock = (e) => {
    e.preventDefault();
    if (pin === ADMIN_PIN) {
      sessionStorage.setItem("jth_admin", "1");
      setUnlocked(true);
      toast.success("Welcome back!");
    } else {
      toast.error("Incorrect PIN.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((en) => (en.id === id ? { ...en, status } : en)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const filtered = useMemo(() => {
    let list = enquiries;
    if (tab !== "all") list = list.filter((e) => e.status === tab);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (e) => e.name.toLowerCase().includes(needle) || e.phone.toLowerCase().includes(needle)
      );
    }
    return list;
  }, [enquiries, tab, q]);

  if (!unlocked) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center px-5 pt-24">
        <form onSubmit={unlock} className="card-royal w-full max-w-sm p-8 text-center" data-testid="admin-pin-form">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 text-gold">
            <LogIn className="h-6 w-6" />
          </span>
          <h1 className="mt-5 font-display text-2xl text-amber-100">Owner's Inbox</h1>
          <p className="mt-1 text-xs text-stone-500">Enter your PIN to view enquiries.</p>
          <input
            type="password"
            inputMode="numeric"
            className="field mt-6 text-center tracking-[0.5em]"
            placeholder="PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            data-testid="admin-pin-input"
          />
          <button type="submit" className="btn-gold mt-4 w-full" data-testid="admin-pin-submit-btn">
            Unlock Inbox
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="mt-2 font-display text-3xl font-semibold text-amber-100 sm:text-4xl">Enquiry Inbox</h1>
        </div>
        <button onClick={load} className="btn-outline-gold !px-5 !py-2.5" data-testid="admin-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
      </div>

      <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="admin-status-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all ${
                tab === t.key
                  ? "border-gold bg-gradient-to-r from-[#BF953F] via-[#F3E5AB] to-[#AA771C] text-[#14120E]"
                  : "border-gold/25 text-stone-300 hover:border-gold/60"
              }`}
            >
              {t.label}
              {t.key !== "all" && (
                <span className="ml-2 opacity-70">
                  {enquiries.filter((e) => e.status === t.key).length}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative md:w-72">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500" />
          <input
            className="field !pl-11"
            placeholder="Search name or phone..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-search-input"
          />
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-gold/15" data-testid="admin-enquiry-table">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-black/40 text-xs uppercase tracking-[0.15em] text-stone-500">
            <tr>
              <th className="px-5 py-4 font-medium">Customer</th>
              <th className="px-5 py-4 font-medium">Event</th>
              <th className="px-5 py-4 font-medium">Message</th>
              <th className="px-5 py-4 font-medium">Received</th>
              <th className="px-5 py-4 font-medium">Status</th>
              <th className="px-5 py-4 font-medium">Reply</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="border-t border-gold/10 align-top transition-colors hover:bg-gold/5" data-testid={`admin-row-${e.id}`}>
                <td className="px-5 py-4">
                  <p className="font-medium text-amber-100">{e.name}</p>
                  <p className="mt-1 text-xs text-stone-400">{e.phone}</p>
                </td>
                <td className="px-5 py-4 text-stone-300">
                  <p>{e.event_type || "—"}</p>
                  <p className="mt-1 text-xs text-stone-500">
                    {e.event_date ? `Date: ${e.event_date}` : "Date: —"} · {e.guests ? `${e.guests} guests` : "Guests: —"}
                  </p>
                </td>
                <td className="max-w-[260px] px-5 py-4 text-stone-300">
                  <p className="line-clamp-3">{e.message || "—"}</p>
                </td>
                <td className="px-5 py-4 text-xs text-stone-400">{fmtDate(e.created_at)}</td>
                <td className="px-5 py-4">
                  <select
                    value={e.status}
                    onChange={(ev) => setStatus(e.id, ev.target.value)}
                    data-testid={`admin-status-select-${e.id}`}
                    className={`rounded-full border bg-black/50 px-3 py-1.5 text-xs uppercase tracking-widest outline-none ${STATUS_STYLE[e.status] || "border-gold/40 text-amber-200"}`}
                  >
                    <option value="new" className="bg-[#14120E]">New</option>
                    <option value="contacted" className="bg-[#14120E]">Contacted</option>
                    <option value="confirmed" className="bg-[#14120E]">Confirmed</option>
                  </select>
                </td>
                <td className="px-5 py-4">
                  <a
                    href={waLink(`Hello ${e.name}, thank you for your enquiry with Jaiswal Tent House regarding ${e.event_type || "your event"}. We would be happy to help!`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-reply-${e.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 px-4 py-2 text-xs font-semibold text-[#25D366] transition-colors hover:bg-[#25D366]/10"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="6" className="px-5 py-14 text-center text-stone-500">
                  {loading ? "Loading enquiries..." : "No enquiries yet. New enquiries from the website will appear here."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-xs text-stone-600">
        Every enquiry submitted on the website lands here and opens pre-filled in the customer's WhatsApp.{" "}
        <Link to="/" className="text-gold hover:underline">Back to site</Link>
      </p>
    </div>
  );
}
